// /en/moj-translator/documents — Wave 67: EN hub of the six MoJ/court document families
import { createFileRoute, Link } from "@tanstack/react-router";
import ServiceAuthorityBlock from "@/components/ServiceAuthorityBlock";
import { ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/en/moj-translator/documents";
const URL = `${SITE}${PATH}`;
const TITLE = "Court & MoJ documents we translate and certify — 6 families | NYC";
const DESC =
  "The six families of Thai court and Ministry-of-Justice documents that need a certified Thai translation under Civil Procedure Code s.46, with the certification order, checklists and FAQs.";

export const Route = createFileRoute("/en/moj-translator/documents/")({
  loader: async () => {
    const { MOJ_DOC_GROUPS } = await import("@/data/moj-doc-groups");
    return {
      __d: { MOJ_DOC_GROUPS },
      groups: MOJ_DOC_GROUPS.map((g) => ({
        slug: g.slug,
        title: g.titleEn,
        faqs: g.faqsEn.map((f) => ({ q: f.q, a: f.a })),
      })),
    };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: ctaTitle(TITLE, PATH, "en") },
      { name: "description", content: ctaDesc(DESC, PATH, "en") },
      { property: "og:title", content: ctaTitle(TITLE, PATH, "en") },
      { property: "og:description", content: ctaDesc(DESC, PATH, "en") },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "en", href: URL },
      { rel: "alternate", hrefLang: "th-TH", href: `${SITE}/moj-translator/documents` },
      { rel: "alternate", hrefLang: "x-default", href: `${SITE}/moj-translator/documents` },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          inLanguage: "en",
          mainEntity: (loaderData?.groups ?? []).flatMap((g) =>
            g.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          ),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "@id": `${URL}#groups`,
          name: "Court and Ministry of Justice document families",
          numberOfItems: (loaderData?.groups ?? []).length,
          itemListElement: (loaderData?.groups ?? []).map((g, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: g.title,
            url: `${URL}/${g.slug}`,
          })),
        }),
      },
    ],
  }),
  component: EnMojDocumentsPage,
});

function EnMojDocumentsPage() {
  const { __d: { MOJ_DOC_GROUPS } } = Route.useLoaderData() as { __d: typeof import("@/data/moj-doc-groups") };
  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm text-muted-foreground">
        <Link to="/en/moj-translator" className="hover:underline">
          MoJ certified translator
        </Link>{" "}
        / Court documents we translate
      </nav>

      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
        Court &amp; Ministry of Justice documents — six families
      </h1>

      <p className="mt-4 text-base leading-8 text-muted-foreground" data-speakable>
        Section 46 of the Thai Civil Procedure Code requires any foreign-language document used in
        proceedings to be filed with a Thai translation certified by the translator. This hub groups the
        documents by how they are used — pleadings, judgments, evidence, enforcement, family status and
        corporate or arbitration files — and sets out the certification order and preparation checklist for
        each. Requirements vary between courts and agencies, so verify with the receiving office before you
        file.
      </p>

      <ul className="mt-8 grid gap-2 sm:grid-cols-2">
        {MOJ_DOC_GROUPS.map((g) => (
          <li key={g.slug}>
            <Link
              to="/en/moj-translator/documents/$group"
              params={{ group: g.slug }}
              className="text-primary hover:underline"
            >
              {g.titleEn}
            </Link>
          </li>
        ))}
      </ul>

      {MOJ_DOC_GROUPS.map((g) => (
        <section key={g.slug} id={g.slug} className="mt-12 scroll-mt-24">
          <h2 className="text-2xl font-semibold">{g.titleEn}</h2>
          <p className="mt-2 text-muted-foreground">{g.summaryEn}</p>
          <div className="mt-4 space-y-4 leading-8">
            {g.bodyEn.slice(0, 2).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <Link
            to="/en/moj-translator/documents/$group"
            params={{ group: g.slug }}
            className="mt-3 inline-block text-primary hover:underline"
          >
            Read the full {g.titleEn} guide →
          </Link>
        </section>
      ))}

      <p className="mt-10 rounded-lg border border-border bg-muted/30 p-4 text-sm leading-7">
        Send us the file list or scans and our team will confirm the exact steps and a realistic timeline for
        your matter — LINE @nycli, +66-83-249-4999, nycli@ilc.ltd.
      </p>
          <ServiceAuthorityBlock topic="moj" locale="en" />
    </main>
  );
}
